import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/api";
import Landing from "@/pages/Landing";
import PortalLogin from "@/pages/PortalLogin";
import AdminLayout from "@/layouts/AdminLayout";
import StaffLayout from "@/layouts/StaffLayout";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import Attendance from "@/pages/Attendance";
import AttendanceReview from "@/pages/AttendanceReview";
import LeaveAdmin from "@/pages/LeaveAdmin";
import Fines from "@/pages/Fines";
import Incentives from "@/pages/Incentives";
import Advances from "@/pages/Advances";
import Payroll from "@/pages/Payroll";
import Reports from "@/pages/Reports";
import Audit from "@/pages/Audit";
import ManualPayroll from "@/pages/ManualPayroll";
import PayrollReports from "@/pages/PayrollReports";
import CompanySettings from "@/pages/CompanySettings";
import Locations from "@/pages/Locations";
import LocationCompliance from "@/pages/LocationCompliance";
import HrAccess from "@/pages/HrAccess";
import Verify from "@/pages/Verify";
import StaffHome from "@/pages/StaffHome";
import StaffPunch from "@/pages/StaffPunch";
import StaffLeave from "@/pages/StaffLeave";
import StaffPayslips from "@/pages/StaffPayslips";
import StaffProfile from "@/pages/StaffProfile";
import "@/App.css";

const PUBLIC_PATHS = new Set(["/", "/hr", "/staff", "/adentry"]);

function Shell() {
  const { user } = useAuth();
  const loc = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0D10] text-white">
        <div className="text-sm tracking-[0.3em] uppercase text-zinc-500">Loading</div>
      </div>
    );
  }

  // Not logged in: only public routes; everything else → /
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/hr" element={<PortalLogin kind="hr" />} />
        <Route path="/staff" element={<PortalLogin kind="staff" />} />
        <Route path="/adentry" element={<PortalLogin kind="admin" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Logged in but landed on a public path? Send them into the correct portal.
  if (PUBLIC_PATHS.has(loc.pathname)) {
    return <Navigate to={isAdminRole(user.role) ? "/dashboard" : "/home"} replace />;
  }

  if (isAdminRole(user.role)) {
    return (
      <Routes>
        <Route element={<AdminLayout />}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/attendance" element={<Attendance />} />
          <Route path="/attendance-review" element={<AttendanceReview />} />
          <Route path="/leave" element={<LeaveAdmin />} />
          <Route path="/fines" element={<Fines />} />
          <Route path="/incentives" element={<Incentives />} />
          <Route path="/advances" element={<Advances />} />
          <Route path="/payroll" element={<Payroll />} />
          <Route path="/manual-payroll" element={<ManualPayroll />} />
          <Route path="/payroll-reports" element={<PayrollReports />} />
          <Route path="/company-settings" element={<CompanySettings />} />
          <Route path="/locations" element={<Locations />} />
          <Route path="/location-compliance" element={<LocationCompliance />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/hr-access" element={<HrAccess />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/verify/:vid" element={<Verify />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route element={<StaffLayout />}>
        <Route path="/home" element={<StaffHome />} />
        <Route path="/punch" element={<StaffPunch />} />
        <Route path="/my-leave" element={<StaffLeave />} />
        <Route path="/my-payslips" element={<StaffPayslips />} />
        <Route path="/profile" element={<StaffProfile />} />
        <Route path="*" element={<Navigate to="/home" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster theme="dark" position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
